import SeoPingPanel from './SeoPingPanel';
import SeoIndexationPanel from './SeoIndexationPanel';
import SeoPerformancePanel from './SeoPerformancePanel';
import PrerenderPanel from './PrerenderPanel';
import TyreSeasonPanel from './TyreSeasonPanel';
import HomeContentPanel from './HomeContentPanel';
import React, { useState, useEffect } from 'react';
import { Building2, Loader2, Save, CheckCircle, AlertCircle, FileText, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { settingsAPI, uploadsAPI, suppliersAPI } from '../services/api';
import ImageUploadField from './ImageUploadField';
import ShoppingFeedsCard from './ShoppingFeedsCard';
import WinterBannerSettingsCard from './WinterBannerSettingsCard';
import GoogleBusinessSettingsCard from './GoogleBusinessSettingsCard';
import { refreshBranding } from './Logo';
import FaviconSyncRow from './FaviconSyncRow';

const FIELDS = [
  ['name', 'Nom commercial *', 'OTOPIECES.COM'],
  ['legal_form', 'Forme juridique', 'SAS'],
  ['address', 'Adresse', '70, rue du Caire'],
  ['postal_code', 'Code postal', '59100'],
  ['city', 'Ville', 'Roubaix'],
  ['country', 'Pays', 'France'],
  ['siret', 'SIRET', '000 000 000 00000'],
  ['vat_number', 'N° TVA intracommunautaire', 'FR00000000000'],
  ['email', 'E-mail', 'info@otopieces.com'],
  ['accountant_email', 'E-mail du comptable — reçoit l\'export Excel automatique le 1er du mois', 'comptable@cabinet.fr'],
  ['phone', 'Téléphone', '+33 6 49 42 10 30'],
  ['website', 'Site web', 'www.otopieces.com'],
  ['facebook_url', 'Page Facebook — icône dans la signature des e-mails et le pied de page', 'https://www.facebook.com/otopieces'],
  ['instagram_url', 'Compte Instagram — icône dans la signature des e-mails et le pied de page', 'https://www.instagram.com/otopieces'],
  ['tiktok_url', 'Compte TikTok — icône dans la signature des e-mails et le pied de page', 'https://www.tiktok.com/@otopieces'],
  ['vat_rate', 'Taux de TVA (%)', '20'],
  ['target_margin_pct', 'Marge cible par défaut (%) — prix conseillé', '35'],
  ['monthly_revenue_target', 'Objectif de CA mensuel (€ TTC) — 0 = aucun', '0'],
  ['monthly_margin_target', 'Objectif de marge mensuelle (€ HT) — 0 = aucun', '0'],
  ['pro_discount_pct', 'Remise compte pro (%) — appliquée aux comptes professionnels validés', '0'],
  ['review_reminder_days', 'Délai rappel avis (jours après livraison) — e-mail automatique', '10'],
  ['review_reminder_days_2', '2e rappel d\'avis (jours après livraison) si aucun avis déposé — 0 = désactivé, doit être > au 1er délai', '21'],
  ['cart_reminder_hours', 'Délai relance panier abandonné (heures après le dernier ajout) — voir onglet « Paniers abandonnés »', '24'],
  ['low_stock_threshold', 'Seuil de stock bas par défaut (unités) — alerte admin + bon de commande ; modifiable par produit', '5'],
  ['opening_hours', 'Horaires du service client (page Contact)', 'Lun–Ven 8h30–18h30 · Sam 9h–13h'],
];

const AdminSettingsPanel = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveCount, setSaveCount] = useState(0);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    settingsAPI.getCompany().then(setForm).catch(() => setMessage({ type: 'error', text: 'Impossible de charger les paramètres' }));
  }, []);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const [recapBusy, setRecapBusy] = useState(false);
  const sendRecap = async () => {
    setRecapBusy(true);
    try {
      const r = await suppliersAPI.sendWeeklyStockRecap();
      setMessage(r.status === 'sent' || r.status === 'simulated' ? { type: 'success', text: `Récap stock envoyé à ${r.to} (${r.count} produit(s) à surveiller)` } : { type: 'error', text: `Récap non envoyé : ${r.status}` });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Envoi impossible' });
    } finally {
      setRecapBusy(false);
    }
  };

  const save = async () => {
    setMessage(null);
    if (!form.name.trim()) { setMessage({ type: 'error', text: 'Le nom commercial est obligatoire' }); return; }
    const vat = parseFloat(form.vat_rate);
    if (isNaN(vat) || vat < 0 || vat > 100) { setMessage({ type: 'error', text: 'Taux de TVA invalide' }); return; }
    setSaving(true);
    try {
      const saved = await settingsAPI.updateCompany({ ...form, vat_rate: vat, target_margin_pct: Math.min(95, Math.max(0, parseFloat(form.target_margin_pct) || 0)),
        monthly_revenue_target: Math.max(0, parseFloat(form.monthly_revenue_target) || 0), monthly_margin_target: Math.max(0, parseFloat(form.monthly_margin_target) || 0), pro_discount_pct: Math.min(90, Math.max(0, parseFloat(form.pro_discount_pct) || 0)), review_reminder_days: Math.min(90, Math.max(1, parseInt(form.review_reminder_days, 10) || 10)), review_reminder_days_2: Math.min(120, Math.max(0, parseInt(form.review_reminder_days_2, 10) || 0)), cart_reminder_hours: Math.min(720, Math.max(1, parseInt(form.cart_reminder_hours, 10) || 24)), low_stock_threshold: Math.max(0, parseInt(form.low_stock_threshold, 10) || 0), reorder_target_qty: Math.max(1, parseInt(form.reorder_target_qty, 10) || 20) });
      setForm(saved);
      await refreshBranding();
      setSaveCount(n => n + 1);
      setMessage({ type: 'success', text: 'Paramètres enregistrés — ils s\'appliquent aux prochaines factures téléchargées' });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Erreur lors de l\'enregistrement' });
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-orange-500" /></div>;

  return (
    <>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Paramètres</h1>
      <div className="bg-white rounded-lg shadow-sm p-6 max-w-3xl" data-testid="company-settings">
        <div className="flex items-center gap-2 mb-1">
          <Building2 size={20} className="text-orange-500" />
          <h2 className="font-semibold text-gray-900">Coordonnées de la société</h2>
        </div>
        <p className="text-sm text-gray-500 mb-6 flex items-center gap-1"><FileText size={14} /> Ces informations apparaissent sur les factures PDF des clients.</p>

        {message && (
          <p data-testid="settings-message" className={`mb-4 text-sm flex items-center gap-2 ${message.type === 'error' ? 'text-red-600' : 'text-green-700'}`}>
            {message.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />} {message.text}
          </p>
        )}

        <div className="mb-6 p-4 border border-gray-200 rounded-lg" data-testid="logo-settings">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Logo du site</h3>
          <p className="text-xs text-gray-500 mb-3">PNG transparent recommandé (hauteur ≈ 80 px). Le logo remplace le logo texte sur le site et les factures ; retirez-le pour revenir au logo texte. La version « fond clair » est optionnelle (connexion, inscription, facture) — sinon le logo principal est utilisé partout.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[['logo_url', 'dark', 'bg-[#1e3a5f]', 'Logo principal — fond sombre (header, footer, admin)'], ['logo_light_url', 'light', 'bg-gray-100', 'Logo fond clair — optionnel (connexion, inscription, facture)']].map(([key, k, bg, label]) => {
              const url = form[key] || (k === 'light' ? form.logo_url : '');
              return (
                <div key={key}>
                  <p className="text-xs font-medium text-gray-700 mb-1">{label}</p>
                  <ImageUploadField label={null} testId={`settings-${key}`} value={form[key] || ''} onChange={(u) => setForm(f => ({ ...f, [key]: u }))} />
                  <div data-testid={`logo-preview-${k}`} className={`${bg} rounded-md p-4 flex items-center justify-center min-h-[80px] mt-2`}>
                    {url
                      ? <img src={uploadsAPI.resolveUrl(url)} alt="Logo" className="no-placeholder h-10 w-auto max-w-[220px] object-contain" />
                      : <span className="text-2xl font-bold"><span className={k === 'dark' ? 'text-white' : 'text-[#1e3a5f]'}>OTO</span><span className="text-orange-500">PIECES.COM</span></span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mb-6 p-4 border border-gray-200 rounded-lg" data-testid="welcome-email-settings">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">E-mail de bienvenue</h3>
          <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" data-testid="settings-welcome_include_password" checked={form.welcome_include_password !== false} onChange={(e) => setForm(f => ({ ...f, welcome_include_password: e.target.checked }))} className="mt-0.5 w-4 h-4 accent-orange-500" />
            <span>Inclure les identifiants (e-mail + mot de passe choisi) dans l'e-mail de bienvenue envoyé au client.<br /><span className="text-xs text-gray-500">Le mot de passe n'est jamais stocké en clair ni transmis à l'administration : il figure uniquement dans l'e-mail que reçoit le client lui-même.</span></span>
          </label>
        </div>

        <div className="mb-6 p-4 border border-gray-200 rounded-lg" data-testid="low-stock-email-settings">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Alertes stock bas par e-mail</h3>
          <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer mb-3">
            <input type="checkbox" data-testid="settings-low_stock_email_enabled" checked={form.low_stock_email_enabled !== false} onChange={(e) => setForm(f => ({ ...f, low_stock_email_enabled: e.target.checked }))} className="mt-0.5 w-4 h-4 accent-orange-500" />
            <span>Envoyer un e-mail dès qu'un produit passe sous son seuil de stock (seuil par défaut ci-dessus, ou seuil propre à la fiche).<br /><span className="text-xs text-gray-500">Une seule alerte par produit, réarmée dès que le stock repasse au-dessus du seuil. La notification cloche/push reste toujours active.</span></span>
          </label>
          <label className="block text-xs font-medium text-gray-600 mb-1">Destinataire — vide = e-mail de la société ({form.email || 'non renseigné'})</label>
          <Input data-testid="settings-low_stock_email_to" type="email" value={form.low_stock_email_to ?? ''} onChange={set('low_stock_email_to')} placeholder="stock@otopieces.com" />
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
            <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" data-testid="settings-low_stock_auto_draft" checked={form.low_stock_auto_draft !== false} onChange={(e) => setForm(f => ({ ...f, low_stock_auto_draft: e.target.checked }))} className="mt-0.5 w-4 h-4 accent-orange-500" />
              <span>Préparer automatiquement un <b>brouillon de bon de commande fournisseur</b> avec les produits sous seuil dès l'alerte.<br /><span className="text-xs text-gray-500">Un brouillon par fournisseur (complété si un brouillon existe déjà) ; à vérifier puis valider dans Fournisseurs › Bons de commande. Les produits sans fournisseur lié sont ignorés.</span></span>
            </label>
            <div className="flex items-center gap-3">
              <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Stock cible après réassort (unités)</label>
              <Input data-testid="settings-reorder_target_qty" type="number" min="1" className="w-28" value={form.reorder_target_qty ?? 20} onChange={set('reorder_target_qty')} />
              <span className="text-xs text-gray-500">Quantité proposée = cible − stock − bons en cours</span>
            </div>
            <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" data-testid="settings-weekly_stock_recap_enabled" checked={form.weekly_stock_recap_enabled !== false} onChange={(e) => setForm(f => ({ ...f, weekly_stock_recap_enabled: e.target.checked }))} className="mt-0.5 w-4 h-4 accent-orange-500" />
              <span>Envoyer chaque <b>lundi 9h</b> un récap des produits en rupture, sous seuil ou proches du seuil (≤ 2× le seuil ou moins de 14 jours de couverture au rythme des ventes).</span>
            </label>
            <button type="button" data-testid="settings-send-weekly-stock-recap" disabled={recapBusy} onClick={sendRecap} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1e3a5f] border border-[#1e3a5f]/30 hover:bg-blue-50 rounded px-3 py-1.5 disabled:opacity-50">{recapBusy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Envoyer le récap maintenant</button>
          </div>
        </div>

        <ShoppingFeedsCard />
        <GoogleBusinessSettingsCard />
        <WinterBannerSettingsCard />

        <div className="mb-6 p-4 border border-gray-200 rounded-lg" data-testid="favicon-settings">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Icône du site (favicon) & aperçu de partage</h3>
          <p className="text-xs text-gray-500 mb-3">Image carrée recommandée (PNG 512×512). Elle s'affiche dans l'onglet du navigateur et sur l'écran d'accueil mobile. L'aperçu de partage (WhatsApp, Facebook, LinkedIn, X) est généré automatiquement à partir du logo et de l'accroche.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <ImageUploadField label={null} testId="settings-favicon_url" value={form.favicon_url || ''} onChange={(u) => setForm(f => ({ ...f, favicon_url: u }))} />
              <div className="flex items-center gap-3 mt-3 bg-gray-100 rounded-md px-3 py-2" data-testid="favicon-preview">
                <div className="flex items-center gap-2 bg-white rounded-t-md px-3 py-1.5 shadow-sm border border-b-0 border-gray-200">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/branding/favicon?v=${encodeURIComponent(form.favicon_url || 'default')}`} alt="" className="w-4 h-4" />
                  <span className="text-xs text-gray-700">OTOPIECES.COM — Pièces auto en ligne</span>
                </div>
                <span className="text-[11px] text-gray-500">aperçu onglet</span>
              </div>
              <FaviconSyncRow />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Accroche de l'image de partage</label>
              <Input data-testid="settings-og_tagline" value={form.og_tagline ?? ''} onChange={set('og_tagline')} placeholder="Pièces détachées auto au meilleur prix" />
              <img
                data-testid="og-preview"
                src={`${process.env.REACT_APP_BACKEND_URL}/api/branding/og-image?v=${encodeURIComponent(`${form.logo_url}|${form.og_tagline}|${saveCount}`)}`}
                alt="Aperçu de partage"
                className="mt-3 w-full rounded-md border border-gray-200 shadow-sm"
              />
              <p className="text-[11px] text-gray-500 mt-1">L'aperçu se met à jour après enregistrement.</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map(([key, label, placeholder]) => (
            <div key={key} className={key === 'address' ? 'md:col-span-2' : ''}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
              <Input data-testid={`settings-${key}`} value={form[key] ?? ''} onChange={set(key)} placeholder={placeholder} type={['vat_rate', 'review_reminder_days', 'review_reminder_days_2', 'cart_reminder_hours', 'low_stock_threshold'].includes(key) ? 'number' : 'text'} />
            </div>
          ))}
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Mention en pied de facture</label>
            <textarea
              data-testid="settings-invoice_footer"
              value={form.invoice_footer || ''}
              onChange={set('invoice_footer')}
              rows={2}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <Button data-testid="settings-save" onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700">
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />} Enregistrer
          </Button>
        </div>
      </div>
      <SeoPingPanel />
      <SeoPerformancePanel />
      <SeoIndexationPanel />
      <HomeContentPanel />
      <PrerenderPanel />
      <TyreSeasonPanel />
    </>
  );
};

export default AdminSettingsPanel;
